import { useCallback, useState } from 'react';
import { Campaign, Status_Enum as Status } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { useQueryClient } from 'react-query';
import { keys, sortCampaigns } from '@frontend/api-bulk-messaging';
import { useBulkMessageEditorContext } from '../bulk-message-editor.context';
import { BACKEND_DATE_TIME_FORMAT } from '../constants';
import { useBulkNavigator } from './use-bulk-navigator';
import { useHandleSaveError } from './use-handle-save-error';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const useDraft = () => {
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // invalidate drafts cache
  const queryClient = useQueryClient();
  const draftsQueryKey = keys.base.listDraftCampaigns;
  const invalidateDrafts = useCallback(
    (draft: Campaign) => {
      const previous = queryClient.getQueryData<Campaign[]>(draftsQueryKey) ?? [];
      const newDrafts = previous.filter((item) => item.campaignId !== draft.campaignId);
      newDrafts.push(draft);
      const sortedNewDrafts = sortCampaigns(newDrafts);
      queryClient.setQueryData(draftsQueryKey, sortedNewDrafts);
      queryClient.invalidateQueries(draftsQueryKey);
    },
    [draftsQueryKey, queryClient]
  );

  const { campaignId, campaignType, getCampaignData, saveAs, setCampaignId } = useBulkMessageEditorContext();
  const navigateTo = useBulkNavigator(campaignType);
  const { handleSaveError } = useHandleSaveError();

  const createHiddenDraft = useCallback(async () => {
    if (!campaignId) {
      try {
        if (!saveAs) return;
        const newCampaign = getCampaignData();
        const newCampaignId = await saveAs(newCampaign, Status.HIDDEN_DRAFT);
        setCampaignId(newCampaignId);
        return newCampaignId;
      } catch (error) {
        handleSaveError(error);
      }
    }
    return campaignId;
  }, [campaignId, getCampaignData, handleSaveError, saveAs, setCampaignId]);

  const saveDraft = useCallback(async () => {
    try {
      if (!saveAs) return;
      const newCampaign = getCampaignData();
      setIsSavingDraft(true);
      const newCampaignId = await saveAs(newCampaign, Status.DRAFT);
      const createdAt = dayjs.utc().format(BACKEND_DATE_TIME_FORMAT);
      const draftCampaignId = campaignId || newCampaignId;
      invalidateDrafts({ ...newCampaign, campaignId: draftCampaignId, createdAt });
      navigateTo.drafts();
    } catch (error) {
      handleSaveError(error);
    } finally {
      setIsSavingDraft(false);
    }
  }, [campaignId, getCampaignData, handleSaveError, invalidateDrafts, navigateTo, saveAs]);

  return {
    createHiddenDraft,
    isSavingDraft,
    saveDraft,
  };
};
